import React from "react";
import { useOutletContext } from "react-router-dom";
import theme from "../../../../theme";
import { Box } from "../../../../components/Box";
import { useFillViewportHeight } from "../../../../hooks/useFillViewportHeight";
import { Flex } from "../../../../components/Flex";
import { useProfileDocuments } from "../../../../data/useProfileDocuments";
import { TitleHeading, Heading, CardLine } from "../../../IndividualView";
import { Profile } from "../../../../types/Profile";
import { Button } from "../../../../components/Button";
import Select, { Option } from "../../../../components/Select";
import { Input } from "../../../../components/Input";
import { notify } from "../../../../utils/notify";
import { queryClient } from "../../../../queryClient";
import { useUploadProfileDocument } from "../../../../data/useUploadProfileDocument";
import { Paragraph } from "../../../../components/Paragraph";
import { Image } from "../../../../components/Image";
import upload from "../../../../assets/upload.svg";
import { H3 } from "../../../../components/Heading";
import deleteIcon from "../../../../assets/settings/delete-red.svg";
import downloadIcon from "../../../../assets/download-blue.svg";
import { useAuth } from "../../../../context/AuthContext";
import { useDeleteProfileDocument } from "../../../../data/useDeleteProfileDocument";
import { ConfirmModal } from "../../../../components/Modals/ConfirmModal";
import { Modal } from "../../../../components/Modal";
import {
  DocumentList,
  DocumentListItem,
} from "../../../../components/DocumentList";
import {
  downloadDocument,
  downloadDocuments,
  fileNameFromUrl,
} from "../../../../utils/downloadDocument";
import { useAllAdditionalDocuments } from "../../../../data/useAllAdditionalDocuments";
import { stripHtmlTags } from "../../../../utils/string";

const MAX_BYTES = 25 * 1024 * 1024;

const ACCEPTED_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/jpeg",
  "image/png",
  "text/plain",
];

const acceptAttr = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt";

type TypeFilter = "individual" | "entity";

export const DocumentsTab = ({
  profile: profileOverride,
}: {
  profile?: Profile;
}) => {
  const { ref: fillRef, height: fillHeight } = useFillViewportHeight();
  const outlet = useOutletContext<{ profile: Profile } | null>();
  const profile = profileOverride ?? outlet?.profile;

  const { authHeader } = useAuth();

  const docsQuery = useProfileDocuments(profile?.id);

  const { mutate: uploadDoc, isLoading: isUploading } =
    useUploadProfileDocument();

  const [isOpen, setIsOpen] = React.useState(false);

  const [file, setFile] = React.useState<File | null>(null);
  const [tooLarge, setTooLarge] = React.useState(false);
  const [docType, setDocType] = React.useState<Option | null>(null);
  const [docTypeTouched, setDocTypeTouched] = React.useState(false);
  const [description, setDescription] = React.useState("");
  const [descriptionTouched, setDescriptionTouched] = React.useState(false);
  const [showValidationErrors, setShowValidationErrors] = React.useState(false);
  const { mutate: deleteDoc } = useDeleteProfileDocument();
  const [docToDelete, setDocToDelete] = React.useState<DocumentListItem | null>(
    null
  );

  const additionalDocType: TypeFilter | undefined =
    profile?._type === "entity"
      ? "entity"
      : profile?._type === "individual"
      ? "individual"
      : undefined;

  const {
    data: additionalDocOptions,
    isLoading: isAdditionalDocOptionsLoading,
  } = useAllAdditionalDocuments(additionalDocType);

  const additionalDocSelectOptions: Option[] =
    additionalDocOptions?.map((d) => ({
      value: d.id,
      label: d.name,
      data: d,
    })) ?? [];

  /** API rows mapped onto the shared list's shape. */
  const listItems: DocumentListItem[] = React.useMemo(
    () =>
      (docsQuery?.data ?? []).map((d: any, idx: number) => ({
        id: d?.id ?? d?.media ?? idx,
        fileName: fileNameFromUrl(d?.media, "-"),
        media: d?.media,
        type: d?.name,
        description: d?.description,
        uploadedOn: d?.created_on,
        raw: d,
      })),
    [docsQuery?.data]
  );

  const resetModal = React.useCallback(() => {
    setFile(null);
    setTooLarge(false);
    setDocType(null);
    setDocTypeTouched(false);
    setDescription("");
    setDescriptionTouched(false);
    setShowValidationErrors(false);
  }, []);

  const close = () => {
    setIsOpen(false);
    resetModal();
  };

  const pickFile = (f: File) => {
    const isAccepted =
      ACCEPTED_MIME.includes(f.type) ||
      acceptAttr
        .split(",")
        .some((ext) => f.name.toLowerCase().endsWith(ext.trim()));

    if (!isAccepted) {
      notify("Unsupported file type.", {
        type: "error",
        position: "bottom-left",
      });
      return;
    }

    const isTooLarge = f.size > MAX_BYTES;
    setTooLarge(isTooLarge);
    setFile(f);
  };

  const onBrowse = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) pickFile(f);
    e.target.value = "";
  };

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  };

  const onSubmit = () => {
    if (!profile?.id) return;

    setShowValidationErrors(true);

    if (!file) {
      notify("Please select a file to upload.", {
        type: "error",
        position: "bottom-left",
      });
      return;
    }

    if (!docType?.value) {
      notify("Please select a document type.", {
        type: "error",
        position: "bottom-left",
      });
      return;
    }

    if (!description.trim()) {
      notify("Please enter a description.", {
        type: "error",
        position: "bottom-left",
      });
      return;
    }

    if (tooLarge) return;

    uploadDoc(
      {
        profileId: profile.id,
        file,
        additional_document: Number(docType?.value),
        description: description.trim(),
      },
      {
        onSuccess: () => {
          notify("Your file has been uploaded successfully!", {
            type: "success",
            position: "bottom-left",
          });

          queryClient.invalidateQueries({
            queryKey: ["profileDocuments", profile.id],
          });

          close();
        },
        onError: () => {
          notify("Upload failed. Please try again.", {
            type: "error",
            position: "bottom-left",
          });
        },
      }
    );
  };

  const docTypeError =
    (showValidationErrors || docTypeTouched) && !docType?.value
      ? "Document type is required"
      : "";

  const descriptionError =
    (showValidationErrors || descriptionTouched) && !description.trim()
      ? "Description is required"
      : "";

  const handleDownload = async (document: DocumentListItem) => {
    try {
      await downloadDocument(
        document.media as string,
        authHeader,
        document.fileName
      );
    } catch {
      notify("Download failed. Please try again.", {
        type: "error",
        position: "bottom-left",
      });
    }
  };

  const downloadAll = () => downloadDocuments(listItems, authHeader);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  const openDeleteModal = (document: DocumentListItem) => {
    setDocToDelete(document);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDocToDelete(null);
  };

  const handleConfirmDelete = () => {
    const id = docToDelete?.raw?.id;

    if (!id) {
      notify("Document id not found.", {
        type: "error",
        position: "bottom-left",
      });
      closeDeleteModal();
      return;
    }

    deleteDoc(
      { id },
      {
        onSuccess: () => {
          notify("Document deleted successfully.", {
            type: "success",
            position: "bottom-left",
          });

          queryClient.invalidateQueries({
            queryKey: ["profileDocuments", profile?.id],
          });

          closeDeleteModal();
        },
        onError: () => {
          notify("Delete failed. Please try again.", {
            type: "error",
            position: "bottom-left",
          });
          closeDeleteModal();
        },
      }
    );
  };

  return (
    <Box ref={fillRef} height={fillHeight} overflowY="auto" overflowX="hidden">
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Flex alignItems="baseline" gap="8">
          <TitleHeading>Documents</TitleHeading>
          {!docsQuery.isLoading && listItems.length > 0 && (
            <Paragraph fontSize={1} color="gray.50" my={0}>
              {listItems.length}
            </Paragraph>
          )}
        </Flex>

        <Flex gap="12" alignItems="center">
          <Button
            onClick={downloadAll}
            variant="blueTextWithBorder"
            isDisabled={!listItems.length || docsQuery.isLoading}
          >
            <Image
              src={downloadIcon}
              alt="Download All Documents"
              height="auto"
              width="22px"
              mr={1}
            />
            <Paragraph fontWeight="500" fontSize={1} color="blue.primary">
              Download All
            </Paragraph>
          </Button>

          <Button onClick={() => setIsOpen(true)}>Upload</Button>
        </Flex>
      </Flex>

      {/* Upload modal */}
      <Modal isOpen={isOpen} onClose={close} width="720px">
        <H3 fontSize={2} mb={2}>
          Upload the file
        </H3>

        {/* Dropzone */}
        {!file ? (
          <>
            <Box
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              border={`1px dashed ${theme.colors.gray[30]}`}
              borderRadius={12}
              p={40}
              minHeight={180}
            >
              <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                height="100%"
                gap="16"
              >
                <Image src={upload} alt="upload icon" />
                <Paragraph my={0}>Drop your file here</Paragraph>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={acceptAttr}
                  onChange={onBrowse}
                  style={{ display: "none" }}
                />

                <Button
                  type="button"
                  variant="primary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Or Browse Files
                </Button>
              </Flex>
            </Box>

            <Flex justifyContent="space-between" mt={0}>
              <CardLine color={theme.colors.muted}>
                Supported formats: pdf, doc, docx, xls, xlsx, jpg, jpeg, png,
                txt.
              </CardLine>
              <CardLine color={theme.colors.muted}>Maximum size: 25MB</CardLine>
            </Flex>
          </>
        ) : (
          <Flex
            justifyContent="space-between"
            alignItems="center"
            background={theme.colors.gray[5]}
            borderRadius={10}
            p={12}
          >
            <Box>
              <Heading fontSize={14}>{file.name}</Heading>
              <CardLine color={theme.colors.muted}>
                {file.type || "File"}
              </CardLine>
            </Box>

            <Image
              style={{ cursor: "pointer" }}
              src={deleteIcon}
              alt="delete icon"
              height="25px"
              width="25px"
              marginRight={"4px"}
              onClick={() => {
                setFile(null);
                setTooLarge(false);
              }}
            />
          </Flex>
        )}

        {/* Error banner */}
        {tooLarge && (
          <Box
            mt={3}
            p={3}
            background={theme.colors.red[50]}
            borderRadius={10}
            border={`1px solid ${theme.colors.red[20]}`}
          >
            <CardLine color={theme.colors.red.primary}>
              The file you are trying to upload is too large.
            </CardLine>
          </Box>
        )}

        {/* Additional details */}
        <Box mt={4}>
          <H3 fontSize={2} mb={2}>
            Additional File Details
          </H3>

          <Flex gap="12" flexWrap="wrap">
            <Box flex="1 1 280px">
              <Select
                label="Document type"
                isRequired
                options={additionalDocSelectOptions}
                value={docType}
                hasError={Boolean(docTypeError)}
                errorMessage={docTypeError}
                placeholder={
                  isAdditionalDocOptionsLoading
                    ? "Loading types..."
                    : "Choose type"
                }
                onChange={(opt: any) => {
                  setDocTypeTouched(true);
                  setDocType(opt);
                }}
                onMenuClose={() => setDocTypeTouched(true)}
              />
            </Box>
          </Flex>

          <Box mt={3}>
            <Input
              label="Description"
              isRequired
              placeholder="Enter text"
              value={description}
              hasError={Boolean(descriptionError)}
              errorMessage={descriptionError}
              onChange={(e: any) => {
                setDescriptionTouched(true);
                setDescription(stripHtmlTags(e.target.value));
              }}
              onBlur={() => setDescriptionTouched(true)}
            />
          </Box>
        </Box>

        <Flex justifyContent="flex-end" gap="12" mt={4}>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            isDisabled={
              !file ||
              tooLarge ||
              isUploading ||
              !docType?.value ||
              !description.trim()
            }
          >
            Submit
          </Button>
        </Flex>
      </Modal>

      <DocumentList
        documents={listItems}
        isLoading={docsQuery.isLoading}
        onDownload={handleDownload}
        onDelete={openDeleteModal}
        emptyMessage="No documents have been uploaded against this profile."
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onRequestClose={closeDeleteModal}
        handleConfirm={handleConfirmDelete}
        title="Delete document?"
        content={`Are you sure you want to delete ${
          docToDelete?.fileName ?? "this document"
        }? This action cannot be undone.`}
      />
    </Box>
  );
};
